import { token } from '$lib/features/auth/authStore';
import { language } from '$lib/features/localization';
import { get, writable } from 'svelte/store';
import { closeToast, showToast } from '../features/app/modals';
import { logger } from '$lib/logger';
import {
  createOnceNotifier,
  createSessionRefresher,
  isTokenExpiring,
} from './session';

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export const REFRESH_URL = new URL(
  '/v2/auth/user/token/refresh',
  API_BASE_URL,
).toString();

const headers = (language, token, sendFile = false) => {
  const headers = {
    'Accept-Language': language,
    Authorization: token ? `Bearer ${token}` : '',
  };

  if (!sendFile) {
    headers['Content-Type'] = 'application/json';
  }

  return headers;
};

const handleResponse = (response, clearOnUnauthorized = true) => {
  // Auto sing out if token is invalid or expired only
  if (
    clearOnUnauthorized &&
    (response.status === 403 || response.status === 401)
  ) {
    clearToken();
    throw { error: 'Unauthorized', status: response.status };
  }
  if (response.status >= 400 && response.status < 500) {
    const message = `${response.statusText} : ${response.url}`;
    logger.warn(message);
  }

  if (response.ok) return response.json().catch(() => response.body);

  return response.json().then((data) => {
    throw { ...data, status: response.status };
  });
};

const clearToken = () => token.set(null);

const sessionRefresher = createSessionRefresher({
  refreshUrl: REFRESH_URL,
  fetchFn: (...args) => fetch(...args),
  onToken: (newToken) => token.set(newToken),
});

let sessionEndingToastId;
const sessionEndingNotifier = createOnceNotifier(() => {
  sessionEndingToastId = showToast(
    'Din session håller på att gå ut. Spara ditt arbete nu.',
    'warning',
    false,
  );
});
// Also used when the session was extended (re-authentication), so the warning can show again later.
export const resetSessionEndingWarning = () => {
  sessionEndingNotifier.reset();
  if (sessionEndingToastId) closeToast(sessionEndingToastId);
};
token.subscribe((value) => {
  if (!value) resetSessionEndingWarning();
});

const NO_REFRESH_PREFIXES = [
  'nurse/login',
  'nurse/bankid',
  'nurse/resetPassword',
];

// NOTE: A rejected login (wrong password, wrong user on re-authentication) says nothing about the current session, so it must not clear it.
const isAuthPath = (path) =>
  NO_REFRESH_PREFIXES.some((prefix) => path.startsWith(prefix));

// NOTE: credentials 'include' lets the browser store and send the httpOnly refresh cookie to the (cross-origin) API.
const authedFetch = async (path, init, { language, sendFile = false }) => {
  const canRefresh = !isAuthPath(path);

  const send = () =>
    fetch(`${API_BASE_URL}/${path}`, {
      ...init,
      credentials: 'include',
      headers: headers(language, get(token), sendFile),
    });

  if (canRefresh && get(token) && isTokenExpiring(get(token))) {
    const refreshedToken = await sessionRefresher.refresh();
    const isTokenStillValid = !isTokenExpiring(get(token), Date.now(), 0);
    if (!refreshedToken && isTokenStillValid) sessionEndingNotifier.notify();
  }

  const response = await send();

  if (
    canRefresh &&
    get(token) &&
    (response.status === 401 || response.status === 403)
  ) {
    const refreshedToken = await sessionRefresher.refresh();
    if (refreshedToken) return send();
  }

  return response;
};

const makeRequest = async (method, url, language, data) => {
  try {
    const response = await authedFetch(
      url,
      { method, body: data ? JSON.stringify(data) : undefined },
      { language },
    );
    return handleResponse(response, !isAuthPath(url));
  } catch (error) {
    if (error?.error !== 'Unauthorized' && error instanceof Error) {
      showToast(error.message, 'error');
    }
    logger.error('An error occurred:', {
      err: error instanceof Error ? error : undefined,
    });
    throw error;
  }
};

const httpMethods = (language) => {
  return {
    get: (url, params) => {
      const searchParams = params ? `?${new URLSearchParams(params)}` : '';
      return makeRequest('GET', `${url}${searchParams}`, language);
    },
    raw: async (url) => {
      try {
        const response = await authedFetch(url, {}, { language });

        if (response.status === 403 || response.status === 401) {
          clearToken();
          throw { error: 'Unauthorized', status: response.status };
        }

        return response;
      } catch (error) {
        if (error?.error !== 'Unauthorized') {
          showToast(error?.message, 'error');
        }
        logger.error('An error occurred:', {
          err: error instanceof Error ? error : undefined,
        });
        throw error;
      }
    },
    sendFile: (url, data) =>
      authedFetch(
        url,
        { method: 'POST', body: data ? data : undefined },
        { language, sendFile: true },
      )
        .then(handleResponse)
        .catch((error) => {
          throw error;
        }),

    post: (url, data) => makeRequest('POST', url, language, data),
    delete: (url, data) => makeRequest('DELETE', url, language, data),
    put: (url, data) => makeRequest('PUT', url, language, data),
    patch: (url, data) => makeRequest('PATCH', url, language, data),

    stream: async (url, data) => {
      const response = await authedFetch(
        url,
        { method: 'POST', body: JSON.stringify(data) },
        { language },
      );
      if (!response.body[Symbol.asyncIterator]) {
        response.body[Symbol.asyncIterator] = () => {
          const reader = response.body.getReader();
          return {
            next: () => {
              return reader.read();
            },
          };
        };
      }
      return response.body;
    },

    getFile: async (url, { fileName, download = true } = {}) => {
      const res = await api().raw(url);

      fileName =
        fileName ||
        res.headers.get('file-name') ||
        res.headers.get('content-disposition');

      const blob = await res.blob();
      const link = window.URL.createObjectURL(blob);

      function downloadFn() {
        const linkElement = document.createElement('a');
        linkElement.href = link;
        linkElement.download = fileName;
        linkElement.click();
        window.URL.revokeObjectURL(link);
      }

      if (download) downloadFn();

      return { fileName, blob, link, res, download: downloadFn };
    },
  };
};

const apiStore = writable(httpMethods(get(language)));
language.subscribe((lang) => apiStore.set(httpMethods(lang)));

export const api = () => {
  return get(apiStore);
};
